'use client';

import { useEffect, useRef } from 'react';
import { useReducedMotion } from 'motion/react';

/**
 * Dalga alanı — React Bits "Waves" (MIT,
 * https://reactbits.dev/backgrounds/waves).
 *
 * Algoritma kaynaktan BİREBİR: dikey çizgilerden bir ızgara kuruluyor, her
 * düğüm Perlin gürültüsünün verdiği açıya göre kendi elipsinde salınıyor ve
 * imleç yaklaştığında yay gibi itilip geri toplanıyor. Bu bileşen WebGL
 * KULLANMIYOR — 2D bağlam yetiyor, çünkü çizilen şey birkaç yüz `lineTo`.
 *
 * ── Kaynaktan sapmalar (`light-rays.tsx` ile aynı kabuk) ──────────────────
 *
 * **Azaltılmış hareket.** Kaynakta koşulsuz `requestAnimationFrame` ve imleç
 * takibi. MotionConfig sonsuz döngüleri kapatmıyor (DESIGN.md §1), o yüzden
 * elle: tercih açıksa tek kare basılıp duruluyor, dinleyici hiç bağlanmıyor.
 * Donmuş alan eksik görünmüyor — desen zamanın her anında tam bir görüntü.
 *
 * **Görünürlük.** Kaynak sekme gizliyken de dönüyor. Burada
 * `IntersectionObserver` ve `visibilitychange` döngüyü DURDURUYOR.
 *
 * **Piksel oranı.** 🔴 Kaynağın en görünür kusuru buydu: `canvas.width`a CSS
 * genişliği yazılıyor, yani retina ekranda tuval yarı çözünürlükte kalıp
 * çizgiler tırtıklı çıkıyor. Burada tampon dpr ile ölçekleniyor ve bağlam
 * `setTransform` ile CSS pikseline geri ayarlanıyor — çizim kodu değişmiyor,
 * çıktı keskin. Tampon MAX_BUFFER_PIXELS ile sınırlı.
 *
 * **Ölçü kaynağı.** Kaynak `window.resize` dinliyor; burada kabın kendisi
 * `ResizeObserver` ile izleniyor — tuvalin yüksekliği sahne değişince de
 * değişiyor, pencere ölçüsü değişmeden.
 *
 * **Tohum.** Kaynak `new Noise(Math.random())` diyor, yani her kurulumda başka
 * bir desen. Burada tohum sabit: geliştirmede efekt iki kez koştuğunda ya da
 * kap yeniden ölçüldüğünde desenin zıplamaması için.
 *
 * **Kendi canvas'ı.** Kaynak JSX'te bir `<canvas>` tutuyor; burada tuval
 * efektin içinde yaratılıp temizlikte götürülüyor — `light-rays` ve
 * `dark-veil` ile aynı sözleşme.
 */

export type WavesProps = {
  className?: string;
  /** Çizgi rengi — herhangi bir CSS rengi (alfa dâhil). */
  lineColor?: string;
  /** CSS pikseli cinsinden çizgi kalınlığı. */
  lineWidth?: number;
  waveSpeedX?: number;
  waveSpeedY?: number;
  waveAmpX?: number;
  waveAmpY?: number;
  /** Çizgiler arası yatay boşluk; küçük değer daha sık alan. */
  xGap?: number;
  /** Çizgi üzerindeki düğümlerin dikey aralığı. */
  yGap?: number;
  friction?: number;
  tension?: number;
  maxCursorMove?: number;
  /** İmleç etkileşimi. Azaltılmış harekette zaten kapanıyor. */
  interactive?: boolean;
};

/** 2.2 Mpx ≈ 1920×1150. */
const MAX_BUFFER_PIXELS = 2_200_000;

/* ── Perlin gürültüsü — kaynaktaki `perlin.js` (Stefan Gustavson) ─────────── */

class Grad {
  constructor(
    readonly x: number,
    readonly y: number,
    readonly z: number,
  ) {}
  dot2(x: number, y: number): number {
    return this.x * x + this.y * y;
  }
}

const GRAD3 = [
  new Grad(1, 1, 0),
  new Grad(-1, 1, 0),
  new Grad(1, -1, 0),
  new Grad(-1, -1, 0),
  new Grad(1, 0, 1),
  new Grad(-1, 0, 1),
  new Grad(1, 0, -1),
  new Grad(-1, 0, -1),
  new Grad(0, 1, 1),
  new Grad(0, -1, 1),
  new Grad(0, 1, -1),
  new Grad(0, -1, -1),
];

/* Ken Perlin'in permütasyon tablosu — sabit, "düzeltilecek" bir şey değil. */
const PERM_SOURCE = [
  151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140, 36, 103, 30, 69, 142,
  8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234, 75, 0, 26, 197, 62, 94, 252, 219, 203,
  117, 35, 11, 32, 57, 177, 33, 88, 237, 149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175, 74, 165,
  71, 134, 139, 48, 27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60, 211, 133, 230, 220, 105, 92,
  41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1, 216, 80, 73, 209, 76, 132, 187, 208,
  89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86, 164, 100, 109, 198, 173, 186, 3, 64, 52, 217,
  226, 250, 124, 123, 5, 202, 38, 147, 118, 126, 255, 82, 85, 212, 207, 206, 59, 227, 47, 16, 58,
  17, 182, 189, 28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44, 154, 163, 70, 221, 153, 101, 155,
  167, 43, 172, 9, 129, 22, 39, 253, 19, 98, 108, 110, 79, 113, 224, 232, 178, 185, 112, 104, 218,
  246, 97, 228, 251, 34, 242, 193, 238, 210, 144, 12, 191, 179, 162, 241, 81, 51, 145, 235, 249, 14,
  239, 107, 49, 192, 214, 31, 181, 199, 106, 157, 184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150,
  254, 138, 236, 205, 93, 222, 114, 67, 29, 24, 72, 243, 141, 128, 195, 78, 66, 215, 61, 156, 180,
];

const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
const lerp = (a: number, b: number, t: number) => (1 - t) * a + t * b;

class Noise {
  private readonly perm = new Uint8Array(512);
  private readonly gradP: Grad[] = new Array<Grad>(512);

  constructor(seed: number) {
    let s = seed;
    if (s > 0 && s < 1) s *= 65536;
    s = Math.floor(s);
    if (s < 256) s |= s << 8;
    for (let i = 0; i < 256; i++) {
      const base = PERM_SOURCE[i] ?? 0;
      const v = (i & 1) === 1 ? base ^ (s & 255) : base ^ ((s >> 8) & 255);
      this.perm[i] = v;
      this.perm[i + 256] = v;
      const g = GRAD3[v % 12] ?? GRAD3[0]!;
      this.gradP[i] = g;
      this.gradP[i + 256] = g;
    }
  }

  perlin2(xIn: number, yIn: number): number {
    let X = Math.floor(xIn);
    let Y = Math.floor(yIn);
    const x = xIn - X;
    const y = yIn - Y;
    X &= 255;
    Y &= 255;

    const py = this.perm[Y] ?? 0;
    const py1 = this.perm[Y + 1] ?? 0;
    const n00 = (this.gradP[X + py] ?? GRAD3[0]!).dot2(x, y);
    const n01 = (this.gradP[X + py1] ?? GRAD3[0]!).dot2(x, y - 1);
    const n10 = (this.gradP[X + 1 + py] ?? GRAD3[0]!).dot2(x - 1, y);
    const n11 = (this.gradP[X + 1 + py1] ?? GRAD3[0]!).dot2(x - 1, y - 1);

    const u = fade(x);
    return lerp(lerp(n00, n10, u), lerp(n01, n11, u), fade(y));
  }
}

type Point = {
  x: number;
  y: number;
  waveX: number;
  waveY: number;
  cx: number;
  cy: number;
  vx: number;
  vy: number;
};

export function Waves({
  className,
  lineColor = 'rgba(0, 0, 0, 0.25)',
  lineWidth = 1,
  waveSpeedX = 0.0125,
  waveSpeedY = 0.005,
  waveAmpX = 32,
  waveAmpY = 16,
  xGap = 10,
  yGap = 32,
  friction = 0.925,
  tension = 0.005,
  maxCursorMove = 100,
  interactive = true,
}: WavesProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const parent = mountRef.current;
    if (!parent) return;

    const canvas = document.createElement('canvas');
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    parent.appendChild(canvas);

    const noise = new Noise(0.42);
    let lines: Point[][] = [];
    let width = 1;
    let height = 1;

    const resize = () => {
      width = Math.max(1, parent.clientWidth);
      height = Math.max(1, parent.clientHeight);
      const dprRaw = Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1, 2);
      let dpr = dprRaw;
      const area = width * height * dpr * dpr;
      if (area > MAX_BUFFER_PIXELS) dpr *= Math.sqrt(MAX_BUFFER_PIXELS / area);

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      /* Çizim CSS pikselinde kalıyor: ölçek bağlamda, kodda değil. */
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      canvas.style.width = '100%';
      canvas.style.height = '100%';

      /* Izgara kutudan GENİŞ: kenarda yarım kalan bir çizgi, dalga onu dışarı
         taşıdığında kadraja girip çıkardı. */
      const oWidth = width + 200;
      const oHeight = height + 30;
      const totalLines = Math.ceil(oWidth / xGap);
      const totalPoints = Math.ceil(oHeight / yGap);
      const xStart = (width - xGap * totalLines) / 2;
      const yStart = (height - yGap * totalPoints) / 2;

      lines = [];
      for (let i = 0; i <= totalLines; i++) {
        const points: Point[] = [];
        for (let j = 0; j <= totalPoints; j++) {
          points.push({
            x: xStart + xGap * i,
            y: yStart + yGap * j,
            waveX: 0,
            waveY: 0,
            cx: 0,
            cy: 0,
            vx: 0,
            vy: 0,
          });
        }
        lines.push(points);
      }
    };

    /* İmleç: ham konum, yumuşatılmış konum, hız ve yön. Hız `vs` yayın
       şiddetini belirliyor — duran imleç alanı itmiyor. */
    const mouse = { x: -10, y: 0, lx: 0, ly: 0, sx: 0, sy: 0, vs: 0, a: 0, set: false };

    const movePoints = (time: number) => {
      for (const points of lines) {
        for (const p of points) {
          const move =
            noise.perlin2((p.x + time * waveSpeedX) * 0.002, (p.y + time * waveSpeedY) * 0.0015) *
            12;
          p.waveX = Math.cos(move) * waveAmpX;
          p.waveY = Math.sin(move) * waveAmpY;

          const dx = p.x - mouse.sx;
          const dy = p.y - mouse.sy;
          const dist = Math.hypot(dx, dy);
          const l = Math.max(175, mouse.vs);
          if (dist < l) {
            const s = 1 - dist / l;
            const f = Math.cos(dist * 0.001) * s;
            p.vx += Math.cos(mouse.a) * f * l * mouse.vs * 0.00065;
            p.vy += Math.sin(mouse.a) * f * l * mouse.vs * 0.00065;
          }

          /* Yay: sıfıra doğru çeken kuvvet + sürtünme. İkisi olmadan itilen
             düğüm geri dönmez, alan kalıcı olarak deforme olurdu. */
          p.vx += (0 - p.cx) * tension;
          p.vy += (0 - p.cy) * tension;
          p.vx *= friction;
          p.vy *= friction;
          p.cx += p.vx * 2;
          p.cy += p.vy * 2;
          p.cx = Math.min(maxCursorMove, Math.max(-maxCursorMove, p.cx));
          p.cy = Math.min(maxCursorMove, Math.max(-maxCursorMove, p.cy));
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.beginPath();
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = lineWidth;
      for (const points of lines) {
        const head = points[0];
        if (!head) continue;
        ctx.moveTo(head.x + head.waveX, head.y + head.waveY);
        for (let i = 0; i < points.length; i++) {
          const p = points[i];
          if (!p) continue;
          const last = i === points.length - 1;
          ctx.lineTo(p.x + p.waveX + (last ? 0 : p.cx), p.y + p.waveY + (last ? 0 : p.cy));
        }
      }
      ctx.stroke();
    };

    resize();
    const ro = new ResizeObserver(() => {
      resize();
      if (reduce) {
        movePoints(0);
        draw();
      }
    });
    ro.observe(parent);

    /* Azaltılmış hareket: döngü yok, imleç yok, tek kare. */
    if (reduce) {
      movePoints(0);
      draw();
      return () => {
        ro.disconnect();
        canvas.remove();
      };
    }

    const track = interactive;
    const onPointerMove = (e: PointerEvent) => {
      const b = parent.getBoundingClientRect();
      mouse.x = e.clientX - b.left;
      mouse.y = e.clientY - b.top;
      if (!mouse.set) {
        mouse.sx = mouse.x;
        mouse.sy = mouse.y;
        mouse.lx = mouse.x;
        mouse.ly = mouse.y;
        mouse.set = true;
      }
    };
    /* Dinleyici KAPTA değil pencerede: kap `pointer-events: none` (altındaki
       düğmeler tıklanabilir kalsın diye) ve olayları hiç almıyor. */
    if (track) window.addEventListener('pointermove', onPointerMove, { passive: true });

    let raf: number | null = null;
    let running = false;
    let visible = true;

    const tick = (t: number) => {
      if (!running) return;
      if (track) {
        mouse.sx += (mouse.x - mouse.sx) * 0.1;
        mouse.sy += (mouse.y - mouse.sy) * 0.1;
        const dx = mouse.x - mouse.lx;
        const dy = mouse.y - mouse.ly;
        const d = Math.hypot(dx, dy);
        mouse.vs += (d - mouse.vs) * 0.1;
        mouse.vs = Math.min(100, mouse.vs);
        mouse.lx = mouse.x;
        mouse.ly = mouse.y;
        mouse.a = Math.atan2(dy, dx);
      }
      movePoints(t);
      draw();
      raf = requestAnimationFrame(tick);
    };
    const play = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(tick);
    };
    const stop = () => {
      running = false;
      if (raf !== null) cancelAnimationFrame(raf);
      raf = null;
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else if (visible) play();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const io = new IntersectionObserver(
      (entries) => {
        visible = !!entries[0]?.isIntersecting;
        if (visible && !document.hidden) play();
        else stop();
      },
      { threshold: [0, 0.01] },
    );
    io.observe(parent);

    play();

    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      if (track) window.removeEventListener('pointermove', onPointerMove);
      canvas.remove();
    };
  }, [
    lineColor,
    lineWidth,
    waveSpeedX,
    waveSpeedY,
    waveAmpX,
    waveAmpY,
    xGap,
    yGap,
    friction,
    tension,
    maxCursorMove,
    interactive,
    reduce,
  ]);

  return <div ref={mountRef} className={className} aria-hidden="true" />;
}
